import { useRoomContext } from "@livekit/components-react";
import { useEffect, useState } from "react";

type Reaction = {
	id: string;
	emoji: string;
	participantName: string;
	x: number;
	y: number;
};

type DataMessage = {
	type: "reaction";
	emoji: string;
	participantName: string;
	timestamp: number;
};

const REACTION_DURATION_MS = 3000;
const REACTION_START_Y = 80;
const REACTION_X_MIN = 10;
const REACTION_X_RANGE = 25;

function createReaction(data: DataMessage): Reaction {
	return {
		id: `${data.timestamp}-${Math.random()}`,
		emoji: data.emoji,
		participantName: data.participantName,
		x: Math.random() * REACTION_X_RANGE + REACTION_X_MIN,
		y: REACTION_START_Y,
	};
}

function useRoomDataMessages() {
	const room = useRoomContext();
	const [reactions, setReactions] = useState<Reaction[]>([]);

	useEffect(() => {
		if (!room) return;

		const addReaction = (reaction: Reaction) => {
			setReactions((prev) => [...prev, reaction]);
			setTimeout(() => {
				setReactions((prev) => prev.filter((r) => r.id !== reaction.id));
			}, REACTION_DURATION_MS);
		};

		const handleDataReceived = (payload: Uint8Array) => {
			try {
				const decoder = new TextDecoder();
				const data = JSON.parse(decoder.decode(payload)) as DataMessage;

				if (data.type === "reaction") {
					addReaction(createReaction(data));
				}
			} catch (error) {
				console.error("Failed to parse data message:", error);
			}
		};

		const handleLocalReaction = (data: DataMessage) => {
			addReaction(createReaction(data));
		};

		room.on("dataReceived", handleDataReceived);
		(room as any).on("localReaction", handleLocalReaction);

		return () => {
			room.off("dataReceived", handleDataReceived);
			(room as any).off("localReaction", handleLocalReaction);
		};
	}, [room]);

	return { reactions };
}

export function RoomOverlay() {
	const { reactions } = useRoomDataMessages();

	return (
		<>
			{/* Reaction Animations */}
			<div className="pointer-events-none fixed inset-0 z-50">
				{reactions.map((reaction) => (
					<div
						className="absolute"
						key={reaction.id}
						style={{
							left: `${reaction.x}%`,
							top: `${reaction.y}%`,
							animation: "float-up 3s ease-out forwards",
						}}
					>
						<div className="flex flex-col items-center gap-1">
							<span className="text-6xl drop-shadow-lg">{reaction.emoji}</span>
							<span className="rounded-full bg-black/70 px-2 py-1 text-white text-xs">
								{reaction.participantName}
							</span>
						</div>
					</div>
				))}
			</div>
		</>
	);
}
